/**
 * AccessLogPanel — het break-glass-logboek: wie heeft in de gegevens van welk
 * kind gekeken, wanneer, en waarom.
 *
 * WAAROM DIT SCHERM ER MOET ZIJN
 *
 * Het logboek werd sinds migratie 0008 wél gevuld, maar er was geen enkele
 * manier om het te lezen. Dat is de duurste vorm van schijnzekerheid: je denkt
 * dat er toezicht is, en de rem waar het hele mechanisme op steunt — de
 * wetenschap dat iemand meekijkt — bestaat niet.
 *
 * DRIE KEUZES
 *
 * 1. De REDEN staat vooraan en volledig, niet afgekapt. Dat is het enige veld
 *    dat een mens moet beoordelen; alle andere kolommen zijn feiten. Een reden
 *    die je moet uitklappen wordt niet gelezen.
 *
 * 2. De leerling is een pseudoniem, uit dezelfde functie als de accountlijst.
 *    Een logboek dat bijhoudt wie er in de gegevens van een kind keek, mag niet
 *    zelf de naam van dat kind prijsgeven — dan is het lezen ervan een nieuwe
 *    inzage. De beheerder staat er wél bij naam: dat is het punt van het spoor.
 *
 * 3. Openstaande vensters staan gemarkeerd. Niet als waarschuwing, maar omdat
 *    "nu open" en "was open" verschillende dingen zijn om op te handelen.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { ShieldAlert, RefreshCw, AlertTriangle, Loader2 } from 'lucide-react';
import { Card } from '../../../../components/ui/Card';
import { listDataAccessGrants, type AccessLogEntry } from '../../../../data/supabase/dataAccess';
import { pseudonymFor } from '../../../shared/learnerPseudonym';
import { formatDateWithTime, formatRelativeTime } from '../../../../utils/formatters';
export function AccessLogPanel() {
  const [entries, setEntries] = useState<AccessLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    setLoading(true);
    const res = await listDataAccessGrants();
    setEntries(res.entries);
    setError(res.ok ? null : res.message ?? 'Ophalen mislukt.');
    setLoading(false);
  }, []);

  // Eén poging bij openen, daarna alleen op verzoek. Automatisch herhalen zou
  // bij een ontbrekende migratie tientallen identieke fouten opleveren, en dan
  // verdrinkt de melding die je moet lezen in de herhaling.
  useEffect(() => {
    void load();
  }, [load]);
  const activeCount = entries.filter(e => e.isActive).length;
  return <section aria-labelledby="access-log-heading" className="space-y-4" data-id="element-7197" data-mp="t624hv8eebc6">
      <Card className="p-5" data-id="element-7198" data-mp="e9ci7hnnc1h2">
        <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-7199" data-mp="ed8qke8ry39m">
          <div className="flex items-start gap-3" data-id="element-7200" data-mp="jba8gvugrwji">
            <span className="mt-0.5 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-slate-900 text-white" data-id="element-7201" data-mp="5ar2du9k70jz">
              <ShieldAlert className="h-5 w-5" aria-hidden="true" data-id="element-7202" data-mp="aa3qeic62do5" />
            </span>
            <div data-id="element-7203" data-mp="1zpa4vwu3p420">
              <h3 id="access-log-heading" className="text-lg font-bold leading-tight text-gray-900" data-id="element-7204" data-mp="1zos18f53znk2">
                Inzage in leerlinggegevens
              </h3>
              <p className="mt-1 max-w-2xl text-sm leading-relaxed text-gray-600" data-id="element-7205" data-mp="2eqqrl3c97rxz">
                Elke keer dat een beheerder de gegevens van een kind opent, komt
                daar een regel bij — met de opgegeven reden. Dit logboek is niet
                te wijzigen of te wissen, ook niet door een beheerder.
                {activeCount > 0 && <>
                    {' '}
                    <span className="font-semibold text-amber-700" data-id="element-7206" data-mp="2xhhvhpdh2q4z">
                      {activeCount}{' '}
                      {activeCount === 1 ? 'venster staat' : 'vensters staan'} nu
                      open.
                    </span>
                  </>}
              </p>
            </div>
          </div>

          <button onClick={() => void load()} disabled={loading} className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50" data-id="element-7207" data-mp="32gb87fkxqd43">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" data-id="element-7208" data-mp="184mt9depeajp" />
            Vernieuwen
          </button>
        </div>
      </Card>

      {error && <div role="alert" className="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3" data-id="element-7209" data-mp="1ehr06pyzw77j">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" aria-hidden="true" data-id="element-7210" data-mp="1ilgkh38n02mr" />
          <p className="text-sm font-semibold text-amber-900" data-id="element-7211" data-mp="1niyvr0a4mtxx">{error}</p>
        </div>}

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm" data-id="element-7212" data-mp="qv2fjwilq0n8">
        <div className="overflow-x-auto" data-id="element-7213" data-mp="uvmaqujm6u6q">
          <table className="w-full text-left text-sm" data-id="element-7214" data-mp="zu60l61bwq4d">
            <thead className="border-b border-gray-100 bg-gray-50 font-medium text-gray-500" data-id="element-7215" data-mp="zsmid9w444zy">
              <tr data-id="element-7216" data-mp="14lnwvgseq2j0">
                <th className="px-4 py-3" data-id="element-7217" data-mp="14m7a4k7442qp">Reden</th>
                <th className="px-4 py-3" data-id="element-7218" data-mp="76a5g5rer69p">Beheerder</th>
                <th className="px-4 py-3" data-id="element-7219" data-mp="73fhi496r3bj">Leerling</th>
                <th className="px-4 py-3" data-id="element-7220" data-mp="c3jrdbfl8zss">Geopend</th>
                <th className="px-4 py-3" data-id="element-7221" data-mp="4l2d6rwrowth">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50" data-id="element-7222" data-mp="4ipycnmb32kn">
              {loading && entries.length === 0 ? <tr data-id="element-7223" data-mp="3vpaidndy1a86">
                  <td colSpan={5} className="px-4 py-12 text-center text-gray-400" data-id="element-7224" data-mp="3qsacbch3g7h2">
                    <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" aria-hidden="true" data-id="element-7225" data-mp="3qu4zkxfu8bob" />
                    Logboek ophalen…
                  </td>
                </tr> : entries.length === 0 ? <tr data-id="element-7226" data-mp="3hsx56qgfzuzi">
                  <td colSpan={5} className="px-4 py-12 text-center text-gray-500" data-id="element-7227" data-mp="3d0vuh8yxacbl">
                    Nog geen inzage geregistreerd. Dat is de verwachte stand:
                    leerlinggegevens zijn standaard afgeschermd, en er komt hier
                    pas een regel bij als iemand een venster opent.
                  </td>
                </tr> : entries.map(entry => <tr key={entry.id} className="transition-colors hover:bg-gray-50" data-id="element-7228" data-mp="2z0l2g2lvmrou">
                    <td className="max-w-md px-4 py-3 text-gray-800" data-id="element-7229" data-mp="2u3chxvd9abkf">
                      {entry.reason}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 font-medium text-gray-700" data-id="element-7230" data-mp="2kgiaw1ifgw17">
                      {entry.adminLabel}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-gray-600" data-id="element-7231" data-mp="2ah9l7kuli3kn">
                      {entry.learnerId ? pseudonymFor(entry.learnerId) : <span className="font-sans italic text-gray-500" data-id="element-7232" data-mp="2fh5pzmb49z19">
                          Alle leerlingen
                        </span>}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-gray-500" data-id="element-7233" data-mp="383gpetmzltwh">
                      <div className="flex flex-col" data-id="element-7234" data-mp="2te2f9jbl78g1">
                        <span data-id="element-7235" data-mp="2td9cetfr1336">{formatDateWithTime(entry.grantedAt)}</span>
                        <span className="text-xs text-gray-400" data-id="element-7236" data-mp="2yccblx91sf38">
                          {formatRelativeTime(entry.grantedAt, true)}
                        </span>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3" data-id="element-7237" data-mp="3qwaumvut67ni">
                      {entry.isActive ? <span className="inline-flex items-center rounded border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-800" data-id="element-7238" data-mp="1fdo775rw7ylp">
                          Nu open
                        </span> : entry.revokedAt ? <span className="inline-flex items-center rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600" data-id="element-7239" data-mp="1k67hntvaadma">
                          Zelf gesloten
                        </span> : <span className="inline-flex items-center rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600" data-id="element-7240" data-mp="wir0nndgy5an">
                          Verlopen
                        </span>}
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </section>;
}